<template>
  <v-dialog
    :model-value="modelValue"
    max-width="860"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card tk-dialog">
      <div class="dialog-header tk-dialog__header">
        <div class="tk-dialog__header-main">
          <div class="dialog-header__icon tk-dialog__icon-wrap">
            <v-icon size="22">mdi-account-multiple-plus-outline</v-icon>
          </div>
          <div class="tk-dialog__head-copy">
            <span class="dialog-header__title">Bulk assign leads</span>
            <span class="tk-dialog__subtitle">
              Filter matching leads, then assign them to an agent (Manual
              campaigns only). Max 500 per request — larger selections are
              chunked automatically.
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body tk-dialog__body">
        <div class="tk-dialog__field">
          <label class="tk-dialog__label">Assign to agent</label>
          <v-select
            v-model="agentId"
            :items="agentOptions"
            item-title="title"
            item-value="value"
            placeholder="Select agent"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            class="tk-dialog__input"
            :disabled="saving"
          />
        </div>

        <div class="tk-dialog__field">
          <div class="tk-dialog__filters-head">
            <label class="tk-dialog__label">Advanced filters</label>
            <v-btn
              variant="text"
              size="small"
              class="text-none"
              :disabled="saving"
              @click="addFilterRow"
            >
              Add filter
            </v-btn>
          </div>
          <p class="tk-dialog__hint">
            Filter locally, then assign. Matching leads are selected automatically.
            Same field → OR. Different fields → AND. Use
            <strong>Add filter</strong> for field rules (status, score, industry…).
          </p>

          <div class="ba-quick">
            <v-select
              v-model="assignmentFilter"
              :items="assignmentOptions"
              item-title="title"
              item-value="value"
              label="Assignment"
              variant="outlined"
              density="compact"
              hide-details
              class="tk-dialog__input"
              :disabled="saving"
            />
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-title="title"
              item-value="value"
              label="Status"
              variant="outlined"
              density="compact"
              hide-details
              class="tk-dialog__input"
              :disabled="saving"
            />
            <v-text-field
              v-model="search"
              placeholder="Search name, phone, email…"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="tk-dialog__input"
              :disabled="saving"
            />
          </div>

          <div class="ba-filters">
            <div
              v-for="(filter, index) in advancedFilters"
              :key="filter.id"
              class="ba-filter-row"
              :class="{ 'ba-filter-row--range': isRangeOperator(filter.operator) }"
            >
              <v-select
                v-model="filter.field"
                :items="filterFieldOptions"
                item-title="label"
                item-value="key"
                placeholder="Field"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="onFilterFieldChange(filter)"
              />
              <v-select
                v-model="filter.operator"
                :items="operatorsForRow(filter)"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="onFilterOperatorChange(filter)"
              />

              <div v-if="isRangeOperator(filter.operator)" class="ba-filter-range">
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMin(filter))"
                  type="date"
                  placeholder="From"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMin(filter)"
                  type="number"
                  placeholder="Min"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMax(filter))"
                  type="date"
                  placeholder="To"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMax(filter)"
                  type="number"
                  placeholder="Max"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="tk-dialog__input"
                  :disabled="saving"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
              </div>
              <v-select
                v-else-if="isMultiOperator(filter.operator) && usesValueSelect(filter)"
                :model-value="filter.values"
                :items="valueOptionsForFilter(filter)"
                multiple
                chips
                closable-chips
                placeholder="Select values"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="filter.values = $event || []"
              />
              <v-combobox
                v-else-if="isMultiOperator(filter.operator)"
                :model-value="filter.values"
                multiple
                chips
                closable-chips
                placeholder="Values"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="filter.values = $event || []"
              />
              <v-text-field
                v-else-if="filterRowKind(filter) === 'date'"
                :model-value="getFilterDateInputValue(getFilterSingleValue(filter))"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-text-field
                v-else-if="filterRowKind(filter) === 'number'"
                :model-value="getFilterSingleValue(filter)"
                type="number"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-select
                v-else-if="usesValueSelect(filter)"
                :model-value="getFilterSingleValue(filter)"
                :items="valueOptionsForFilter(filter)"
                placeholder="Select value"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="tk-dialog__input"
                :disabled="saving"
                no-data-text="No values found for this field"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />
              <v-text-field
                v-else
                :model-value="getFilterSingleValue(filter)"
                placeholder="Value"
                variant="outlined"
                density="compact"
                hide-details
                class="tk-dialog__input"
                :disabled="saving"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />

              <v-btn
                icon
                variant="text"
                size="small"
                :disabled="saving"
                @click="removeFilterRow(index)"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div class="tk-dialog__field">
          <div class="tk-dialog__filters-head">
            <label class="tk-dialog__label">
              Matching leads
              <span class="tk-dialog__label-hint">
                {{ visibleLeads.length }} match · {{ selectedIds.length }} selected
              </span>
            </label>
            <div class="ba-toolbar">
              <v-btn
                variant="text"
                size="small"
                class="text-none"
                :disabled="saving || !visibleLeads.length"
                @click="selectAllVisible"
              >
                Select all matches
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                class="text-none"
                :disabled="saving || !visibleLeads.length"
                @click="selectUnassigned"
              >
                Select unassigned
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                class="text-none"
                :disabled="saving || !selectedIds.length"
                @click="clearSelectionManual"
              >
                Clear selection
              </v-btn>
            </div>
          </div>

          <div class="ba-list" role="list">
            <label
              v-for="lead in visibleLeads"
              :key="lead.requestId || lead.id"
              class="ba-row"
            >
              <input
                type="checkbox"
                :checked="selectedIds.includes(lead.requestId)"
                :disabled="saving"
                @change="toggleLead(lead.requestId, $event.target.checked)"
              />
              <span class="ba-row__main">
                <strong>{{ leadName(lead) }}</strong>
                <span class="ba-row__meta">
                  {{ lead.phone || lead.email || lead.requestId || '—' }}
                  <template v-if="lead.statusLabel"> · {{ lead.statusLabel }}</template>
                  <template v-if="lead.leadScore != null"> · Score {{ lead.leadScore }}</template>
                </span>
              </span>
              <span class="ba-row__agent">
                {{ lead.assignedAgentName || lead.assignedAgentId || 'Unassigned' }}
              </span>
            </label>
            <p v-if="!visibleLeads.length" class="tk-dialog__hint ba-empty">
              No leads match the current filters.
            </p>
          </div>
        </div>

        <div v-if="resultErrors.length" class="tk-dialog__unmatched">
          <p class="tk-dialog__section-title">Failed rows</p>
          <div class="tk-dialog__unmatched-table">
            <div class="tk-dialog__unmatched-head">
              <span>Lead</span>
              <span>Agent</span>
              <span>Error</span>
            </div>
            <div
              v-for="(row, idx) in resultErrors"
              :key="`${row.request_id}-${idx}`"
              class="tk-dialog__unmatched-row"
            >
              <span>{{ row.request_id || '—' }}</span>
              <span>{{ agentLabel(row.agent_id) }}</span>
              <span>{{ row.error || '—' }}</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer tk-dialog__footer">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-none tk-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="text-none btn-primary-cta tk-dialog__save"
          rounded="lg"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          Assign {{ selectedIds.length || '' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { bulkAssignLeadsToAgents } from '@/api/campaignsApi'
import {
  buildCampaignFilterFieldOptions,
  campaignFilterFieldKind,
  createEmptyCampaignFilter,
  findCampaignFilterFieldDef,
  getFilterDateInputValue,
  getFilterRangeMax,
  getFilterRangeMin,
  getFilterSingleValue,
  isSelectFilterField,
  leadMatchesCampaignFilters,
  operatorsForFilterField,
  setFilterRangeMax,
  setFilterRangeMin,
  setFilterSingleValue,
  buildFilterValueOptions,
} from '@/utils/campaignFilters'

defineOptions({ name: 'BulkAssignLeadsDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  campaignId: { type: String, default: '' },
  leads: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  contexts: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'saved', 'error'])

const search = ref('')
const assignmentFilter = ref('unassigned')
const statusFilter = ref('')
const advancedFilters = ref([])
const agentId = ref('')
const selectedIds = ref([])
const saving = ref(false)
const resultErrors = ref([])
/** When true, changing filters auto-selects every visible match. */
const autoSelectMatches = ref(true)

const assignmentOptions = [
  { value: 'any', title: 'Any assignment' },
  { value: 'unassigned', title: 'Unassigned only' },
  { value: 'assigned', title: 'Assigned only' },
]

const agentOptions = computed(() =>
  (Array.isArray(props.agents) ? props.agents : []).map((agent) => ({
    title: agent.name || agent.email || agent.id,
    value: String(agent.id),
  })),
)

const statusOptions = computed(() => {
  const seen = new Set()
  for (const lead of Array.isArray(props.leads) ? props.leads : []) {
    const status = String(lead?.status ?? '').trim().toUpperCase()
    if (status) seen.add(status)
  }
  return [
    { value: '', title: 'All statuses' },
    ...[...seen].sort().map((status) => ({
      value: status,
      title: status.replace(/_/g, ' '),
    })),
  ]
})

const filterFieldOptions = computed(() =>
  buildCampaignFilterFieldOptions(props.contexts),
)

const visibleLeads = computed(() => {
  const q = String(search.value ?? '').trim().toLowerCase()
  const status = String(statusFilter.value ?? '').trim().toUpperCase()
  const list = Array.isArray(props.leads) ? props.leads : []

  return list.filter((lead) => {
    const assigned = Boolean(String(lead.assignedAgentId ?? '').trim())
    if (assignmentFilter.value === 'unassigned' && assigned) return false
    if (assignmentFilter.value === 'assigned' && !assigned) return false

    if (status && String(lead.status ?? '').trim().toUpperCase() !== status) {
      return false
    }

    if (
      !leadMatchesCampaignFilters(
        lead,
        advancedFilters.value,
        filterFieldOptions.value,
      )
    ) {
      return false
    }

    if (!q) return true
    return [
      leadName(lead),
      lead.phone,
      lead.email,
      lead.requestId,
      lead.assignedAgentName,
      lead.statusLabel,
      lead.status,
      lead.industry,
      lead.typeOfBusiness,
    ].some((value) => String(value ?? '').toLowerCase().includes(q))
  })
})

const canSubmit = computed(
  () =>
    Boolean(String(props.campaignId ?? '').trim()) &&
    Boolean(agentId.value) &&
    selectedIds.value.length > 0 &&
    !saving.value,
)

function leadName(lead) {
  return (
    String(lead?.name ?? '').trim() ||
    String(lead?.contact_name ?? '').trim() ||
    String(lead?.requestId ?? '').trim() ||
    'Lead'
  )
}

function agentLabel(id) {
  const key = String(id ?? '').trim()
  const match = agentOptions.value.find((row) => row.value === key)
  return match?.title || key || '—'
}

function filterRowKind(filter) {
  const def = findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
  return campaignFilterFieldKind(def)
}

function filterFieldDef(filter) {
  return findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
}

function usesValueSelect(filter) {
  return isSelectFilterField(filterFieldDef(filter))
}

function valueOptionsForFilter(filter) {
  return buildFilterValueOptions(filterFieldDef(filter), props.leads)
}

function operatorsForRow(filter) {
  const def = findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
  return operatorsForFilterField(def)
}

function isRangeOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'BETWEEN'
}

function isMultiOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'IN'
}

function onFilterFieldChange(filter) {
  const ops = operatorsForRow(filter)
  if (!ops.some((op) => op.value === filter.operator)) {
    filter.operator = ops[0]?.value || 'EQUALS'
  }
  filter.values = []
}

function onFilterOperatorChange(filter) {
  if (isRangeOperator(filter.operator)) {
    filter.values = [getFilterRangeMin(filter), getFilterRangeMax(filter)]
    return
  }
  if (isMultiOperator(filter.operator)) {
    filter.values = Array.isArray(filter.values) ? filter.values : []
    return
  }
  const single = getFilterSingleValue(filter)
  filter.values = single === '' || single == null ? [] : [single]
}

function addFilterRow() {
  advancedFilters.value = [...advancedFilters.value, createEmptyCampaignFilter()]
}

function removeFilterRow(index) {
  const next = advancedFilters.value.filter((_, i) => i !== index)
  advancedFilters.value = next.length ? next : [createEmptyCampaignFilter()]
}

function selectAllVisible() {
  autoSelectMatches.value = true
  selectedIds.value = visibleLeads.value
    .map((lead) => String(lead.requestId ?? '').trim())
    .filter(Boolean)
}

function selectUnassigned() {
  autoSelectMatches.value = false
  selectedIds.value = visibleLeads.value
    .filter((lead) => !String(lead.assignedAgentId ?? '').trim())
    .map((lead) => String(lead.requestId ?? '').trim())
    .filter(Boolean)
}

function clearSelectionManual() {
  autoSelectMatches.value = false
  selectedIds.value = []
}

function pruneSelectionToVisible() {
  const visible = new Set(
    visibleLeads.value
      .map((lead) => String(lead.requestId ?? '').trim())
      .filter(Boolean),
  )
  selectedIds.value = selectedIds.value.filter((id) => visible.has(id))
}

function toggleLead(requestId, checked) {
  const id = String(requestId ?? '').trim()
  if (!id) return
  autoSelectMatches.value = false
  if (checked) {
    if (!selectedIds.value.includes(id)) selectedIds.value = [...selectedIds.value, id]
    return
  }
  selectedIds.value = selectedIds.value.filter((item) => item !== id)
}

function close() {
  if (saving.value) return
  emit('update:modelValue', false)
}

async function submit() {
  if (!canSubmit.value) return
  saving.value = true
  resultErrors.value = []
  try {
    const assignments = selectedIds.value.map((request_id) => ({
      request_id,
      agent_id: agentId.value,
    }))
    const response = await bulkAssignLeadsToAgents({
      task_id: props.campaignId,
      assignments,
    })
    resultErrors.value = Array.isArray(response.errors) ? response.errors : []

    if (response.status === 'ERROR' && !response.succeeded) {
      emit('error', response.message || 'Bulk assign failed')
      return
    }

    emit('saved', {
      message: response.message,
      succeeded: response.succeeded,
      failed: response.failed,
      errors: resultErrors.value,
      assignments,
      agentId: agentId.value,
    })

    if (!resultErrors.value.length) {
      emit('update:modelValue', false)
    }
  } finally {
    saving.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    search.value = ''
    assignmentFilter.value = 'unassigned'
    statusFilter.value = ''
    advancedFilters.value = [createEmptyCampaignFilter()]
    agentId.value = ''
    resultErrors.value = []
    autoSelectMatches.value = true
    // Defer so visibleLeads reflects reset filters first.
    queueMicrotask(() => selectAllVisible())
  },
)

watch(
  visibleLeads,
  () => {
    if (autoSelectMatches.value) {
      selectAllVisible()
      return
    }
    pruneSelectionToVisible()
  },
)
</script>

<style scoped>
.tk-dialog :deep(.dialog-header.tk-dialog__header) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.tk-dialog__header-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.tk-dialog :deep(.dialog-header__icon.tk-dialog__icon-wrap) {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  flex-shrink: 0;
}

.tk-dialog__head-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.tk-dialog__subtitle {
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog :deep(.dialog-body.tk-dialog__body) {
  padding: 20px 24px 10px;
}

.tk-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 18px;
  min-width: 0;
}

.tk-dialog__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__label-hint {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  opacity: 0.75;
}

.tk-dialog__hint {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tk-dialog__unmatched {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.08);
}

.tk-dialog__section-title {
  margin: 0 0 10px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--y-text-muted, #64748b);
}

.tk-dialog__unmatched-table {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 160px;
  overflow: auto;
}

.tk-dialog__unmatched-head,
.tk-dialog__unmatched-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 8px;
  font-size: 0.72rem;
}

.tk-dialog__unmatched-head {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #92400e;
}

.tk-dialog :deep(.dialog-footer.tk-dialog__footer) {
  padding: 14px 24px 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.ba-quick {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.3fr);
  gap: 10px;
  margin: 8px 0 12px;
}

.ba-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
  padding: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.ba-filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.4fr) 36px;
  gap: 8px;
  align-items: center;
}

.ba-filter-row--range {
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.4fr) 36px;
}

.ba-filter-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.ba-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.ba-list {
  max-height: 300px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 12px;
}

.ba-empty {
  padding: 14px 12px;
}

.ba-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) minmax(0, 0.8fr);
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
  cursor: pointer;
}

.ba-row:last-child {
  border-bottom: none;
}

.ba-row__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ba-row__main strong {
  font-size: 0.86rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ba-row__meta,
.ba-row__agent {
  font-size: 0.75rem;
  color: var(--y-text-muted, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ba-row__agent {
  text-align: right;
}

@media (max-width: 720px) {
  .ba-quick,
  .ba-filter-row,
  .ba-filter-row--range {
    grid-template-columns: 1fr;
  }
}
</style>
